/**
 * Internationalization (i18n) utilities
 * Provides translation functionality for the application UI
 */

import type { MapLanguage } from '../types';

/**
 * Translation keys for all translatable strings
 */
export interface TranslationKeys {
  // Controls
  controls: {
    maxTime: string;
    maxTimeMinutes: string;
    travelMode: string;
    speed: string;
    mapStyle: string;
    language: string;
    share: string;
    play: string;
    pause: string;
    restart: string;
    loading: string;
    showControls: string;
    hideControls: string;
    expandControls: string;
    collapseControls: string;
    selectLocationHint: string;
  };
  // Travel modes
  travelModes: {
    driving: string;
    walking: string;
    cycling: string;
    transit: string;
  };
  // Speed options
  speed: {
    verySlow: string;
    slow: string;
    half: string;
    normal: string;
    fast: string;
    veryFast: string;
  };
  // Map styles
  mapStyles: {
    streets: string;
    satellite: string;
  };
  // Languages
  languages: {
    en: string;
    'zh-Hans': string;
    'zh-Hant': string;
    es: string;
    fr: string;
    de: string;
    ja: string;
    ko: string;
  };
  // Address input
  addressInput: {
    label: string;
    placeholder: string;
    noResults: string;
    loading: string;
  };
  // Error display
  error: {
    retry: string;
    dismiss: string;
    somethingWentWrong: string;
    browserNotSupported: string;
    usageLimitReached: string;
  };
  // Progress
  progress: {
    minutes: string;
  };
  // Share
  share: {
    urlCopied: string;
    shareUrl: string;
  };
  // Email Gate / Authentication
  emailGate: {
    title: string;
    subtitle: string;
    emailPlaceholder: string;
    emailLabel: string;
    sendButton: string;
    sendingButton: string;
    checkEmailTitle: string;
    checkEmailMessage: string;
    checkEmailInstruction: string;
    checkEmailHelp: string;
    emailRequired: string;
    emailInvalid: string;
    domainRestriction: string;
    domainNotAllowed: string;
    typoSuggestion: string;
    rateLimitExceeded: string;
    invalidEmailFormat: string;
    failedToSend: string;
    magicLinkFailed: string;
    testMeButton: string;
    bannerText: string;
    betaTag: string;
  };
  // Test Code Authentication
  testCode: {
    title: string;
    subtitle: string;
    placeholder: string;
    button: string;
    haveCodeLink: string;
    useEmailLink: string;
    invalid: string;
    expired: string;
    limitReached: string;
    optionalEmailTitle: string;
    optionalEmailSubtitle: string;
    optionalEmailPlaceholder: string;
    skipButton: string;
    submitEmailButton: string;
  };
  // Usage Indicator
  usageIndicator: {
    template: string; // Template with {count} placeholder
    loading: string;
  };
  // Disclaimer Screen
  disclaimer: {
    loading: string;
    title: string;
    content: string; // Main disclaimer paragraph(s)
    personalStance?: string; // Additional paragraph for Chinese only
    consent: string; // Consent statement
  };
  // Intro Tour
  intro: {
    title: string;
    message: string;
    step1: string;
    step2: string;
    step3: string;
    step4: string;
    step5: string;
    next: string;
    skip: string;
    gotIt: string;
    startTour: string;
  };
}

/**
 * Translation data structure
 */
export type Translations = Record<MapLanguage, TranslationKeys>;

/**
 * Default language (English)
 */
export const DEFAULT_LANGUAGE: MapLanguage = 'en';

/**
 * All supported languages
 */
export const SUPPORTED_LANGUAGES: MapLanguage[] = [
  'en',
  'zh-Hans',
  'zh-Hant',
  'es',
  'fr',
  'de',
  'ja',
  'ko',
];

/**
 * Get language from browser/system preferences
 * 
 * Detection priority:
 * 1. navigator.languages (user's preferred languages in order)
 * 2. navigator.language (primary language)
 * 3. navigator.userLanguage (IE fallback)
 * 4. Default to English
 */
export function getBrowserLanguage(): MapLanguage {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  // Get all available language preferences
  const languages: string[] = [];
  
  // Modern browsers: navigator.languages (array of preferred languages)
  if (navigator.languages && navigator.languages.length > 0) {
    languages.push(...navigator.languages);
  }
  
  // Primary language
  if (navigator.language) {
    languages.push(navigator.language);
  }
  
  // IE fallback
  if ((navigator as any).userLanguage) {
    languages.push((navigator as any).userLanguage);
  }
  
  // System language (if available)
  if ((navigator as any).systemLanguage) {
    languages.push((navigator as any).systemLanguage);
  }

  // If no languages found, default to English
  if (languages.length === 0) {
    return DEFAULT_LANGUAGE;
  }

  // Check each language preference in order
  for (const lang of languages) {
    if (!lang) continue;

    const langLower = lang.toLowerCase();
    
    // Chinese language detection (most complex)
    if (langLower.startsWith('zh')) {
      // Check for traditional Chinese indicators
      if (
        langLower.includes('tw') || // Taiwan
        langLower.includes('hk') || // Hong Kong
        langLower.includes('mo') || // Macau
        langLower.includes('hant') || // Traditional script
        langLower === 'zh-tw' ||
        langLower === 'zh-hk' ||
        langLower === 'zh-mo'
      ) {
        return 'zh-Hant';
      }
      // Check for simplified Chinese indicators
      if (
        langLower.includes('cn') || // China
        langLower.includes('sg') || // Singapore
        langLower.includes('hans') || // Simplified script
        langLower === 'zh-cn' ||
        langLower === 'zh-sg'
      ) {
        return 'zh-Hans';
      }
      // Generic 'zh' - default to simplified
      return 'zh-Hans';
    }

    // Map other languages by code
    const langCode = langLower.split('-')[0].split('_')[0]; // Handle both 'en-US' and 'en_US'
    
    const langMap: Record<string, MapLanguage> = {
      'en': 'en',
      'es': 'es',
      'fr': 'fr',
      'de': 'de',
      'ja': 'ja',
      'ko': 'ko',
    };

    if (langMap[langCode]) {
      return langMap[langCode];
    }
  }

  // No match found, default to English
  return DEFAULT_LANGUAGE;
}

